import React, { useEffect, useState } from 'react';
import { BarChart3, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const monthLabel = (m) => MONTHS_FR[Number(m.slice(5, 7)) - 1];

const Trend = ({ monthly, max }) => (
  <div className="flex items-end gap-1 h-8" title={monthly.map((m) => `${monthLabel(m.month)} : ${m.count}`).join(' · ')}>
    {monthly.map((m) => (
      <div key={m.month} className="w-3 rounded-sm bg-[#C86B51]" style={{ height: `${max ? Math.max(8, (m.count / max) * 100) : 8}%`, opacity: m.count ? 1 : 0.2 }} />
    ))}
  </div>
);

const Total = ({ label, value, testId }) => (
  <div className="card-default">
    <p className="text-sm text-[#5C6A61]">{label}</p>
    <p className="text-2xl font-semibold text-[#2A312C] mt-1" data-testid={testId}>{value}</p>
  </div>
);

const ThresholdEditor = ({ value, onSaved }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const save = async () => {
    try {
      await adminApi.setOutOfZoneThreshold(Number(v));
      toast.success('Seuil mis à jour');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seuil invalide');
    }
  };
  return (
    <div className="flex items-center gap-2 text-sm text-[#5C6A61]" data-testid="ooz-threshold-editor">
      <span>Seuil d'intérêt : </span>
      <Input type="number" min={1} value={v} onChange={(e) => setV(e.target.value)} className="w-20 h-9" data-testid="ooz-threshold-input" />
      <span>clients distincts</span>
      <Button size="sm" variant="outline" onClick={save} disabled={Number(v) === value || Number(v) < 1} data-testid="ooz-threshold-save-btn">Enregistrer</Button>
    </div>
  );
};

const ThresholdAlert = ({ c }) => (
  <div className="rounded-xl border border-[#C86B51] bg-[#C86B51]/10 p-4 flex items-start gap-3" data-testid={`ooz-threshold-alert-${c.postal_code}`}>
    <Flag className="w-5 h-5 text-[#C86B51] mt-0.5 shrink-0" />
    <div>
      <p className="font-semibold text-[#2A312C]">{c.city} ({c.postal_code})</p>
      <p className="text-sm text-[#2A312C]">Cette commune atteint le seuil d'intérêt pour une éventuelle ouverture de zone.</p>
      <p className="text-sm text-[#5C6A61] mt-1">{c.clients} clients distincts · {c.requests} demande{c.requests > 1 ? 's' : ''} · {c.authorized} adresse{c.authorized > 1 ? 's' : ''} déjà autorisée{c.authorized > 1 ? 's' : ''} en exception</p>
    </div>
  </div>
);

export const OutOfZoneStats = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const load = () => adminApi.getOutOfZoneStats().then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  if (!data) return null;
  const reached = data.communes.filter((c) => c.threshold_reached);
  const max = Math.max(0, ...data.communes.flatMap((c) => c.monthly.map((m) => m.count)));

  return (
    <div className="space-y-4" data-testid="ooz-stats">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#C86B51]" />
        <h2 className="text-lg font-semibold text-[#2A312C]">Demandes hors zone par commune</h2>
      </div>
      <ThresholdEditor value={data.threshold} onSaved={load} />
      {reached.length > 0 && <div className="space-y-2" data-testid="ooz-threshold-alerts">{reached.map((c) => <ThresholdAlert key={`${c.city}-${c.postal_code}`} c={c} />)}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Total label="Demandes (client + adresse)" value={data.totals.requests} testId="ooz-stats-total-requests" />
        <Total label="Clients distincts" value={data.totals.clients} testId="ooz-stats-total-clients" />
        <Total label="Adresses autorisées" value={data.totals.authorized} testId="ooz-stats-total-authorized" />
      </div>
      {data.communes.length === 0 ? (
        <p className="text-sm text-[#5C6A61]" data-testid="ooz-stats-empty">Aucune demande hors zone enregistrée pour le moment.</p>
      ) : (
        <div className="card-default overflow-x-auto">
          <table className="w-full text-sm" data-testid="ooz-stats-table">
            <thead>
              <tr className="text-left text-[#5C6A61] border-b border-[#E3DED5]">
                <th className="py-2 px-3">Commune</th><th className="py-2 px-3">Demandes</th><th className="py-2 px-3">Tentatives</th>
                <th className="py-2 px-3">Clients distincts</th><th className="py-2 px-3">Autorisées</th>
                <th className="py-2 px-3">Évolution ({monthLabel(data.months[0])} → {monthLabel(data.months[data.months.length - 1])})</th>
              </tr>
            </thead>
            <tbody>
              {data.communes.map((c) => (
                <tr key={`${c.city}-${c.postal_code}`} className={`border-b border-[#E3DED5] ${c.threshold_reached ? 'bg-[#C86B51]/10' : ''}`} data-testid={`ooz-stats-row-${c.postal_code}`}>
                  <td className="py-2 px-3 font-medium text-[#2A312C]">
                    {c.threshold_reached && <Flag className="inline w-4 h-4 text-[#C86B51] mr-1" data-testid={`ooz-threshold-flag-${c.postal_code}`} />}
                    {c.city} <span className="text-[#5C6A61] font-normal">({c.postal_code})</span>
                  </td>
                  <td className="py-2 px-3">{c.requests}</td>
                  <td className="py-2 px-3">{c.attempts}</td>
                  <td className="py-2 px-3">{c.clients}</td>
                  <td className="py-2 px-3">{c.authorized}</td>
                  <td className="py-2 px-3"><Trend monthly={c.monthly} max={max} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#5C6A61] mt-3">Vue agrégée par commune : aucune adresse exacte n'est affichée. Une demande = un client et une adresse ; les tentatives répétées sont comptées à part.</p>
        </div>
      )}
    </div>
  );
};
